import React, { CSSProperties, PropsWithChildren } from "react";

import "./AnimatedBackground.css";

const setCSSVar = (name: string, value: string) => ({ [name]: value });

export type AnimatedBackgroundMode = "light" | "dark" | "light-prominent" | "black";

export interface AnimatedBackgroundProps {
  mode?: AnimatedBackgroundMode;
  durationSeconds?: number;
  className?: string;
  style?: CSSProperties;
  backgroundConfig?: AnimatedBackgroundConfigType;
}

export function AnimatedBackground(props: PropsWithChildren<AnimatedBackgroundProps>) {
  const {
    mode = "light",
    durationSeconds = 20,
    className,
    style,
    children,
    backgroundConfig,
  } = props;

  const brandColor = "rgb(var(--repeatmd-brand-color, 29, 27, 167))";

  // Check if browser supports relative color syntax
  const supportsRelativeColor =
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("color", "rgb(from white r g b)");

  const lightTintColor = supportsRelativeColor
    ? "rgb(from rgb(var(--repeatmd-brand-tint, 35, 95, 215)) calc(r + (255 - r) * 0.5) calc(g + (255 - g) * 0.5) calc(b + (255 - b) * 0.5))"
    : "color-mix(in srgb, rgb(var(--repeatmd-brand-tint, 35, 95, 215)) 50%, white)";

  const darkTintColor = supportsRelativeColor
    ? "rgb(from rgb(var(--repeatmd-brand-tint, 35, 95, 215)) calc(r * 0.65) calc(g * 0.65) calc(b * 0.65))"
    : "color-mix(in srgb, rgb(var(--repeatmd-brand-tint, 35, 95, 215)) 65%, rgb(var(--repeatmd-brand-color, 29, 27, 167)))";

  const darkerBrandColor = supportsRelativeColor
    ? "rgb(from rgb(var(--repeatmd-brand-color, 29, 27, 167)) calc(r * 0.75) calc(g * 0.75) calc(b * 0.75))"
    : "color-mix(in srgb, rgb(var(--repeatmd-brand-color, 29, 27, 167)) 75%, transparent)";

  const superLightBrandColor = supportsRelativeColor
    ? "rgb(from rgb(var(--repeatmd-brand-color, 29, 27, 167)) calc(r + (255 - r) * 0.95) calc(g + (255 - g) * 0.95) calc(b + (255 - b) * 0.95))"
    : "color-mix(in srgb, rgb(var(--repeatmd-brand-color, 29, 27, 167)) 5%, white)";

  // Mode-specific configurations
  const modeConfigs: Record<AnimatedBackgroundMode, AnimatedBackgroundConfigType> = {
    "light": {
      color1: brandColor,
      color2: lightTintColor,
      baseColor: "#ffffff",
      tint1Percent: 25,
      tint2Percent: 25,
      spot1SizePercent: 45,
      spot2SizePercent: 50,
      vignetteOpacity: 1,
    },
    "dark": {
      color1: brandColor,
      color2: darkTintColor,
      baseColor: darkerBrandColor,
      tint1Percent: 100,
      tint2Percent: 95,
      spot1SizePercent: 95,
      spot2SizePercent: 90,
      vignetteOpacity: 0,
    },
    "light-prominent": {
      color1: brandColor,
      color2: lightTintColor,
      baseColor: superLightBrandColor,
      tint1Percent: 30,
      tint2Percent: 30,
      spot1SizePercent: 60,
      spot2SizePercent: 55,
      vignetteOpacity: 0,
    },
    "black": {
      color1: "rgba(100, 100, 130, 1)",
      color2: "rgba(80, 80, 110, 1)",
      baseColor: "rgba(40, 40, 55, 1)",
      tint1Percent: 60,
      tint2Percent: 55,
      spot1SizePercent: 70,
      spot2SizePercent: 65,
      vignetteOpacity: 0,
    },
  };

  const config: AnimatedBackgroundConfigType =
    backgroundConfig ?? modeConfigs[mode] ?? modeConfigs.light;

  const customStyle: CSSProperties = {
    ...(style ?? {}),
    ...setCSSVar("--abg-color-1", config.color1),
    ...setCSSVar("--abg-color-2", config.color2),
    ...setCSSVar("--abg-base", config.baseColor),
    ...setCSSVar("--abg-duration", `${durationSeconds}s`),
    ...setCSSVar("--abg-tint-1", `${config.tint1Percent}%`),
    ...setCSSVar("--abg-tint-2", `${config.tint2Percent}%`),
    ...setCSSVar("--abg-spot1-size", `${config.spot1SizePercent}%`),
    ...setCSSVar("--abg-spot2-size", `${config.spot2SizePercent}%`),
    ...setCSSVar("--abg-vignette-opacity", `${config.vignetteOpacity}`),
  };

  return (
    <div
      className={`animated-background animated-background--${mode}${
        className ? ` ${className}` : ""
      }`}
      style={customStyle}
    >
      {children}
    </div>
  );
}

export type AnimatedBackgroundConfigType = {
  color1: string;
  color2: string;
  baseColor: string;
  tint1Percent: number;
  tint2Percent: number;
  spot1SizePercent: number;
  spot2SizePercent: number;
  vignetteOpacity: number;
};
